import { useCallback } from 'react';
import { CaseStudyItem, ContentBlock, ContentBlockType } from '@/types/case-study';
import { useCaseStudy } from '@/hooks/useCaseStudy';

interface UseContentBlockOperationsProps {
  caseStudy: CaseStudyItem;
  setCaseStudy: React.Dispatch<React.SetStateAction<CaseStudyItem>>;
  activeSection: string | null;
  isNewCaseStudy: boolean;
}

interface UseContentBlockOperationsReturn {
  handleAddContentBlock: (type: ContentBlockType) => Promise<void>;
  handleUpdateContentBlock: (sectionId: string, blockId: string, content: Record<string, unknown>) => Promise<void>;
  handleRemoveContentBlock: (sectionId: string, blockId: string) => Promise<void>;
  getDefaultContentForType: (type: ContentBlockType) => Record<string, unknown>;
}

export const useContentBlockOperations = ({
  caseStudy,
  setCaseStudy,
  activeSection,
  isNewCaseStudy,
}: UseContentBlockOperationsProps): UseContentBlockOperationsReturn => {
  const { updateSection } = useCaseStudy();

  const getDefaultContentForType = useCallback((type: ContentBlockType): Record<string, unknown> => {
    switch (type) {
      case "text":
        return { text: "" };
      case "image":
        return { url: "", caption: "" };
      case "video":
        return { url: "", caption: "", type: "url" };
      case "gallery":
        return { images: [] };
      case "line-break":
        return {};
      case "quote":
        return { text: "", author: "" };
      case "callout":
        return { variant: "info", text: "" };
      default:
        return {};
    }
  }, []);

  const handleAddContentBlock = useCallback(async (type: ContentBlockType) => {
    if (!activeSection) return;

    const newBlock: ContentBlock = {
      id: crypto.randomUUID(),
      type,
      content: getDefaultContentForType(type),
      order: 0, // Will be set based on current blocks
    };

    // Update local state immediately
    setCaseStudy(prev => ({
      ...prev,
      sections: prev.sections.map(section => {
        if (section.id === activeSection) {
          const currentBlocks = section.content_blocks || [];
          const updatedBlocks = [...currentBlocks, { ...newBlock, order: currentBlocks.length }];
          return {
            ...section,
            content_blocks: updatedBlocks,
          };
        }
        return section;
      }),
    }));

    // Save to database if not a new case study
    if (!isNewCaseStudy) {
      try {
        const currentSection = caseStudy.sections.find(section => section.id === activeSection);
        if (currentSection) {
          const currentBlocks = currentSection.content_blocks || [];
          const updatedBlocks = [...currentBlocks, { ...newBlock, order: currentBlocks.length }];
          await updateSection.mutateAsync({
            caseStudyId: caseStudy.id,
            sectionId: activeSection,
            updates: { content_blocks: updatedBlocks },
          });
        }
      } catch (error) {
        console.error('Error adding content block:', error);
      }
    }
  }, [activeSection, getDefaultContentForType, setCaseStudy, isNewCaseStudy, caseStudy.sections, caseStudy.id, updateSection]);

  const handleUpdateContentBlock = useCallback(async (sectionId: string, blockId: string, content: Record<string, unknown>) => {
    // Update local state immediately
    setCaseStudy(prev => ({
      ...prev,
      sections: prev.sections.map(section => {
        if (section.id === sectionId) {
          const updatedBlocks = (section.content_blocks || []).map(block =>
            block.id === blockId ? { ...block, content } : block
          );
          return { ...section, content_blocks: updatedBlocks };
        }
        return section;
      }),
    }));

    // Save to database if not a new case study
    if (!isNewCaseStudy) {
      try {
        // Get the updated section with the new content_blocks
        const updatedSection = caseStudy.sections.find(section => section.id === sectionId);
        if (updatedSection) {
          const updatedBlocks = (updatedSection.content_blocks || []).map(block =>
            block.id === blockId ? { ...block, content } : block
          );
          await updateSection.mutateAsync({
            caseStudyId: caseStudy.id,
            sectionId,
            updates: { content_blocks: updatedBlocks },
          });
        }
      } catch (error) {
        console.error('Error updating content block:', error);
      }
    }
  }, [setCaseStudy, isNewCaseStudy, caseStudy.sections, caseStudy.id, updateSection]);

  const handleRemoveContentBlock = useCallback(async (sectionId: string, blockId: string) => {
    // Update local state immediately
    setCaseStudy(prev => ({
      ...prev,
      sections: prev.sections.map(section => {
        if (section.id === sectionId) {
          const updatedBlocks = (section.content_blocks || []).filter(block => block.id !== blockId);
          return { ...section, content_blocks: updatedBlocks };
        }
        return section;
      }),
    }));

    // Save to database if not a new case study
    if (!isNewCaseStudy) {
      try {
        const currentSection = caseStudy.sections.find(section => section.id === sectionId);
        if (currentSection) {
          const updatedBlocks = (currentSection.content_blocks || []).filter(block => block.id !== blockId);
          await updateSection.mutateAsync({
            caseStudyId: caseStudy.id,
            sectionId,
            updates: { content_blocks: updatedBlocks },
          });
        }
      } catch (error) {
        console.error('Error removing content block:', error);
      }
    }
  }, [setCaseStudy, isNewCaseStudy, caseStudy.sections, caseStudy.id, updateSection]);

  return {
    handleAddContentBlock,
    handleUpdateContentBlock,
    handleRemoveContentBlock,
    getDefaultContentForType,
  };
};
